import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { useAuth } from '@/store/auth'

/**
 * Where the SSO's own redirects land: an email-verification link (`token`)
 * or a hosted-login handoff (`sso_token`). Either way there's a token in the
 * query string to exchange for a session; this page does that once and gets
 * out of the way, so it never needs to know which flow sent it here.
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams()
  const { completeSsoExchange } = useAuth()
  const [failed, setFailed] = useState(false)
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    const token = params.get('sso_token') ?? params.get('token')
    if (!token) {
      setFailed(true)
      return
    }
    completeSsoExchange(token).catch(() => setFailed(true))
  }, [params, completeSsoExchange])

  if (failed) {
    return (
      <AuthLayout title="Sign-in link expired" subtitle="This link is no longer valid." backTo="/login">
        <Link
          to="/login"
          className="block w-full rounded-full bg-brand py-3 text-center text-[14.5px] font-semibold text-white shadow-brand transition hover:bg-brand-dark"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Signing you in…" subtitle="One moment while we finish setting up your session.">
      <div className="flex items-center justify-center py-4">
        <div
          aria-hidden
          className="h-6 w-6 animate-spin rounded-full border-2 border-brand-line border-t-brand"
        />
      </div>
    </AuthLayout>
  )
}
